/**
 * Setup checklist for new workspaces — renders when the inbox has zero
 * tickets. Shows a big copyable support address and a short list of
 * "next steps" deep-linked into Tools / Settings / Team.
 *
 * We persist step completion in localStorage keyed per-org so switching
 * orgs gets a fresh checklist.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '@/components/Toast';
import { CoreIcon } from '@/components/CoreIcon';

interface Step {
  key: string;
  label: string;
  body: string;
  to?: string;
  external?: string;
  icon: string;
}

export function SetupChecklist({
  orgId,
  orgSlug,
  orgName,
}: {
  orgId: string;
  orgSlug: string;
  orgName: string;
}) {
  const toast = useToast();
  const storageKey = `edgemail:checklist:${orgId}`;
  const [done, setDone] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) ?? '{}'); }
    catch { return {}; }
  });

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(done));
  }, [done, storageKey]);

  const toggle = (key: string) => setDone((d) => ({ ...d, [key]: !d[key] }));

  const supportAddress = `${orgSlug}@edgemail.cloud`;
  const copy = async () => {
    await navigator.clipboard.writeText(supportAddress);
    toast.success('Copied', supportAddress);
    setDone((d) => ({ ...d, address: true }));
  };

  const steps: Step[] = [
    {
      key: 'tone',
      label: 'Give the AI your brand voice',
      body: 'Set tone, signature, and house rules so replies sound like you.',
      to: '/app/settings',
      icon: 'speech',
    },
    {
      key: 'tool',
      label: 'Connect your first tool',
      body: 'Let the agent look up customers in Stripe / HubSpot, create Linear issues, or call your own API.',
      to: '/app/tools',
      icon: 'puzzle',
    },
    {
      key: 'domain',
      label: 'Bring your own domain (optional)',
      body: 'Route support@yourcompany.com through edgemail instead of the default slug address.',
      to: '/app/settings',
      icon: 'link',
    },
    {
      key: 'team',
      label: 'Invite teammates',
      body: 'Add people with Agent or Admin access. Typing indicators prevent collisions on the same ticket.',
      to: '/app/team',
      icon: 'user-plus',
    },
    {
      key: 'test',
      label: 'Send your first test email',
      body: `Send anything to ${supportAddress} from a real inbox — you'll see the draft appear here in seconds.`,
      icon: 'send',
    },
  ];

  const completed = Object.values(done).filter(Boolean).length + (done.address ? 0 : 0);
  // Progress counts address-copy + steps-checked.
  const total = steps.length + 1;
  const pct = Math.round(((done.address ? 1 : 0) + steps.filter((s) => done[s.key]).length) / total * 100);

  return (
    <div className="h-full overflow-y-auto p-8">
      <div className="max-w-3xl mx-auto">
        <div className="mono-label text-accent mb-2">Welcome, {orgName}</div>
        <h1 className="text-4xl font-normal tracking-sub leading-heading mb-2">
          Get your AI support agent online in 5 minutes.
        </h1>
        <p className="text-sm text-muted-foreground leading-body max-w-prose">
          No tickets yet — that&apos;s expected. Here&apos;s how to wire up
          your first one. Steps are optional; check them off as you go.
        </p>

        {/* Progress bar */}
        <div className="mt-6 mb-8">
          <div className="flex items-center justify-between mono-label mb-1.5">
            <span>Setup progress</span>
            <span>{pct}%</span>
          </div>
          <div className="h-1.5 rounded-sm bg-muted overflow-hidden">
            <div
              className="h-full bg-accent transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Primary: copy the support address */}
        <section className="card p-5 mb-4">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-sm bg-accent text-accent-foreground grid place-items-center shrink-0">
              <CoreIcon name="envelope-letter" size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-normal tracking-sub leading-heading">
                Your support address
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5 mb-3">
                Emails to this address land in this inbox. Forward it from
                your existing support inbox, or publish it on your site.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <code className="px-3 py-2 rounded-sm bg-muted font-mono text-sm">
                  {supportAddress}
                </code>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={copy}
                  aria-label={`Copy ${supportAddress}`}
                >
                  <CoreIcon name="copy" size={14} />
                  Copy
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Checklist items */}
        <ul className="space-y-2">
          {steps.map((s) => {
            const isDone = Boolean(done[s.key]);
            return (
              <li key={s.key} className="card p-4">
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={isDone}
                    aria-label={`Mark "${s.label}" as ${isDone ? 'not done' : 'done'}`}
                    onClick={() => toggle(s.key)}
                    className={
                      'mt-0.5 h-5 w-5 rounded-sm border grid place-items-center shrink-0 ' +
                      (isDone
                        ? 'bg-accent border-accent text-accent-foreground'
                        : 'bg-surface border-border text-transparent hover:border-foreground')
                    }
                  >
                    <CoreIcon name="check-alt" size={12} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <CoreIcon name={s.icon} size={16} className="text-muted-foreground" />
                      <div className={
                        'font-medium ' + (isDone ? 'line-through text-muted-foreground' : '')
                      }>
                        {s.label}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-body mt-1">{s.body}</p>
                    {s.to && (
                      <Link
                        to={s.to}
                        className="inline-flex items-center gap-1 text-sm mt-2 hover:text-accent"
                      >
                        Open
                        <CoreIcon name="arrow-right" size={12} />
                      </Link>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        <p className="mono-label text-muted-foreground mt-8">
          New emails show up here automatically. You can dismiss this
          screen by sending any test message to your support address.
        </p>
      </div>
    </div>
  );
}
